'use client';

import type { ChainId } from '@morpho-org/blue-sdk';
import type { ReactTable } from '@tanstack/react-table';
import { safeParseNumber } from '@morpho-org/blue-sdk-viem';
import { InterfaceChainUtils, toBigInt } from '@morpho-org/dapp-core';
import { TableNoFilterResults } from '@morpho-org/ui-v6/client';
import type { DashboardBorrowPositionsFilters } from '@/helpers/filter/table';
import type { UserBorrowPositionFacets } from '@/hooks/positions/userBorrowPositionsQuery';
import { MobileMarketPositionCard } from '@/components/common/MobileBorrowCard';
import { getParsedValue } from '@/helpers/formatters';
import { getMarketUrl, MarketPageTabValues } from '@/helpers/routing';
import { useViewedAccountAddress } from '@/hooks/useViewedAccountAddress';
import type { BorrowPositionsTableFeatures } from '../config/BorrowPositionsTable.columns';
import type { BorrowPositionsTableRow } from '../config/BorrowPositionsTable.types';
import DashboardBorrowPositionsFiltersComponent from './DashboardBorrowPositionsFilters';
interface DashboardBorrowPositionsMobileViewProps {
  table: ReactTable<BorrowPositionsTableFeatures, BorrowPositionsTableRow>;
  filters?: DashboardBorrowPositionsFilters;
  onFiltersChange?: (filters: DashboardBorrowPositionsFilters) => void;
  positionChainIds?: ChainId[];
  loading: boolean;
  disabled?: boolean;
  totalPositionsCount: number;
  facets?: UserBorrowPositionFacets;
}
const DashboardBorrowPositionsMobileView = ({
  table,
  filters,
  onFiltersChange,
  positionChainIds,
  loading,
  disabled,
  totalPositionsCount,
  facets
}: DashboardBorrowPositionsMobileViewProps) => {
  // 'use no memo': TanStack keeps this object's reference while table state (sorting, rows) changes.
  'use no memo';

  const {
    isSelf
  } = useViewedAccountAddress();
  const hasFilteredResults = table.getRowModel().rows.length > 0;
  return <div data-testid="dashboard-borrow-positions-list" className="gap-sm flex flex-col">
      <DashboardBorrowPositionsFiltersComponent positionChainIds={positionChainIds} filters={filters} onFiltersChange={onFiltersChange} disabled={disabled || loading} facets={facets} />
      {!hasFilteredResults ? <TableNoFilterResults withBorder type="markets" count={totalPositionsCount} clearFilters={() => onFiltersChange?.({
      collateralAssetIdsFilter: [],
      loanAssetIdsFilter: [],
      chainIdsFilter: []
    })} /> : <>
          {table.getRowModel().rows.map(row => {
        const {
          collateral,
          borrow
        } = row.original;
        const collateralSymbol = row.original.market.collateralAsset?.symbol;
        const marketUrl = collateralSymbol ? getMarketUrl({
          identifier: InterfaceChainUtils.getIdentifierById(row.original.market.chainId),
          marketId: row.original.market.marketId,
          collateralSymbol,
          loanSymbol: row.original.market.loanAsset.symbol,
          mobileTab: isSelf ? MarketPageTabValues.MyPosition : undefined
        }) : undefined;
        return <MobileMarketPositionCard key={row.id} href={marketUrl} hideActionButtons={!isSelf} collateral={{
          token: collateral.token ?? null,
          amount: toBigInt(collateral.amount) ?? null,
          usdValue: getParsedValue(collateral.value, safeParseNumber)
        }} loan={{
          token: borrow.token,
          amount: toBigInt(borrow.amount) ?? null,
          usdValue: getParsedValue(borrow.value, safeParseNumber)
        }} lltv={toBigInt(row.original.health.lltv)} marketId={row.original.market.marketId} netApy={row.original.netApy.avgNetBorrowApy} apy={row.original.netApy.avgBorrowApy} rewards={row.original.netApy.rewards} points={row.original.netApy.points} warnings={collateral.warnings} ltv={toBigInt(row.original.health.ltv) ?? undefined} utilization={row.original.market.utilization ?? undefined} displayNetwork />;
      })}
        </>}
    </div>;
};
export default DashboardBorrowPositionsMobileView;